import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import {
  clearTboHotelSyncCheckpoint,
  formatTboHotelSyncCheckpoint,
  loadTboHotelSyncCheckpoint,
  saveTboHotelSyncCheckpoint,
  type TboHotelSyncCheckpoint,
} from './tbo-hotel-sync-checkpoint';
import * as tboApi from './tbo.api';

import type { TboHotelSyncRequestInput } from '@utazzo/shared';

export type TboHotelSyncUiState =
  | { status: 'idle' }
  | {
      status: 'running';
      progress: tboApi.TboHotelSyncProgress;
      totals: tboApi.TboHotelSyncTotals;
    }
  | {
      status: 'paused';
      checkpoint: TboHotelSyncCheckpoint;
      reason: 'stopped' | 'error';
      message?: string;
    };

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};

const isSyncAborted = (err: unknown): boolean =>
  axios.isCancel(err) || (err instanceof DOMException && err.name === 'AbortError');

export const useTboHotelSync = () => {
  const qc = useQueryClient();
  const abortRef = useRef<AbortController | null>(null);
  const [uiState, setUiState] = useState<TboHotelSyncUiState>({ status: 'idle' });
  const [savedCheckpoint, setSavedCheckpoint] = useState<TboHotelSyncCheckpoint | null>(() =>
    loadTboHotelSyncCheckpoint(),
  );

  useEffect(() => {
    const cp = loadTboHotelSyncCheckpoint();
    if (cp) {
      setSavedCheckpoint(cp);
      setUiState((prev) =>
        prev.status === 'running' ? prev : { status: 'paused', checkpoint: cp, reason: 'stopped' },
      );
    }
  }, []);

  const refreshCheckpoint = useCallback(() => {
    const cp = loadTboHotelSyncCheckpoint();
    setSavedCheckpoint(cp);
    return cp;
  }, []);

  const run = useCallback(
    async (input: TboHotelSyncRequestInput, mode: 'fresh' | 'resume') => {
      const cp = mode === 'resume' ? loadTboHotelSyncCheckpoint() : null;
      if (mode === 'resume' && !cp) {
        toast.error('No saved sync progress to resume.');
        return;
      }

      if (mode === 'fresh') {
        clearTboHotelSyncCheckpoint();
        setSavedCheckpoint(null);
      }

      const request: TboHotelSyncRequestInput = cp
        ? {
            countryCode: cp.countryCode,
            ...(cp.tboCityId ? { tboCityId: cp.tboCityId } : {}),
            cityIndex: cp.cityIndex,
            offset: cp.offset,
          }
        : input;

      const abort = new AbortController();
      abortRef.current = abort;

      try {
        const result = await tboApi.runTboHotelSync(request, {
          signal: abort.signal,
          initialTotals: cp
            ? {
                upserted: cp.upserted,
                errors: cp.errors,
                processedTotal: cp.processedTotal,
              }
            : undefined,
          onProgress: (progress, totals) => {
            setUiState({ status: 'running', progress, totals });
          },
          onCheckpoint: (checkpoint) => {
            saveTboHotelSyncCheckpoint(checkpoint);
            setSavedCheckpoint(checkpoint);
          },
        });

        clearTboHotelSyncCheckpoint();
        setSavedCheckpoint(null);
        setUiState({ status: 'idle' });
        qc.invalidateQueries({ queryKey: ['tbo', 'hotels'] });
        toast.success(
          `TBO hotels synced: ${result.upserted} upserted` +
            (result.errors > 0 ? ` (${result.errors} errors)` : ''),
        );
        return result;
      } catch (err) {
        const checkpoint = refreshCheckpoint();
        if (isSyncAborted(err)) {
          if (checkpoint) {
            setUiState({ status: 'paused', checkpoint, reason: 'stopped' });
            toast.info('Sync stopped. Resume anytime from saved progress.', { duration: 5000 });
          } else {
            setUiState({ status: 'idle' });
            toast.info('Sync stopped.');
          }
          return;
        }

        const message = extractMessage(err) ?? 'TBO hotel sync failed';
        if (checkpoint) {
          setUiState({ status: 'paused', checkpoint, reason: 'error', message });
          toast.error(`${message} - progress saved; tap Resume to continue.`, { duration: 8000 });
        } else {
          setUiState({ status: 'idle' });
          toast.error(message);
        }
        throw err;
      } finally {
        abortRef.current = null;
      }
    },
    [qc, refreshCheckpoint],
  );

  const startFresh = useCallback((input: TboHotelSyncRequestInput) => run(input, 'fresh'), [run]);
  const resume = useCallback(() => {
    const cp = loadTboHotelSyncCheckpoint();
    if (!cp) {
      toast.error('No saved sync progress to resume.');
      return Promise.resolve();
    }
    return run(
      {
        countryCode: cp.countryCode,
        ...(cp.tboCityId ? { tboCityId: cp.tboCityId } : {}),
      },
      'resume',
    );
  }, [run]);

  const stop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const discardCheckpoint = useCallback(() => {
    clearTboHotelSyncCheckpoint();
    setSavedCheckpoint(null);
    setUiState({ status: 'idle' });
    toast.success('Saved sync progress discarded.');
  }, []);

  return {
    uiState,
    savedCheckpoint,
    isRunning: uiState.status === 'running',
    startFresh,
    resume,
    stop,
    discardCheckpoint,
    formatCheckpoint: formatTboHotelSyncCheckpoint,
  };
};
